/**
 * useQuickCreatePolling — quickcreate-async B 方案 (2026-05-25).
 *
 * 给 apps/xingye /create/edit 用. mount 时检查 router.query.create_task_id, 有就启
 * 2.5s 轮询 chat-agent /create/task/get; 5 态 status 按下面策略处理.
 *
 * 5 态处理 (chat-agent 后端约定, BigBoss 19:47 + 19:54 verified):
 *   - pending     → 静默继续轮询
 *   - image_done  → hot-swap dispatch(updateImgInfo({base_img_url: image_url})), 继续轮询
 *   - voice_done  → hot-swap dispatch(mergeVoiceInfo({voice_id})), 继续轮询
 *   - done        → useAddQuery({npc_id}) 加 URL query (shallow:true), 由 caller 的 useEffect
 *                   监听 query.npc_id 变化触发 dispatch(getWorkspaceNpcDetail({npc_id})) 整体覆盖.
 *                   返 done state 让 caller 知道停止 polling.
 *   - failed      → 弹 Toast (caller 自定义) + 跳回 /agent/quick-create
 *
 * Prior art: apps/agent/src/pages/BatchPublishUs/index.tsx:180-202 (2.5s + cancelled flag + cleanup)
 *
 * TIMEOUT 300s 兜底 (跟 AssetAdmin POLL_TIMEOUT_MS 对齐).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';

import {
  postGetNpcStudioCreateTask,
  type GetNpcStudioCreateTaskResp,
} from '../../api/npcStudio';
import { mergeVoiceInfo, updateImgInfo, useAppDispatch } from '../../state';
import { useAddQuery } from '../../hooks/useAddQuery';

const POLL_INTERVAL_MS = 2500;
const POLL_TIMEOUT_MS = 300 * 1000; // 5 min

export type QuickCreatePollingStatus =
  | 'idle'
  | 'pending'
  | 'image_done'
  | 'voice_done'
  | 'done'
  | 'failed'
  | 'timeout';

export interface UseQuickCreatePollingResult {
  status: QuickCreatePollingStatus;
  /** done 时填: 真 npc_id (chat-agent publish 完成拿到) */
  npcId: number;
  /** failed 时填: 用户可读错因 */
  errorMsg: string;
}

/**
 * @param onFailed 调用方决定 failed 时怎么 UI 反馈 (e.g. message.error + router.push)
 *   推荐: 弹 Toast 含 error_msg + window.location.href = '/agent/quick-create'
 */
export function useQuickCreatePolling(
  onFailed?: (errorMsg: string) => void
): UseQuickCreatePollingResult {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const addQuery = useAddQuery();

  const [status, setStatus] = useState<QuickCreatePollingStatus>('idle');
  const [npcId, setNpcId] = useState<number>(0);
  const [errorMsg, setErrorMsg] = useState<string>('');

  // 防止 strict-mode 双 mount 启动两次 polling
  const startedRef = useRef(false);
  // failed callback 用 ref 避免 deps 变 retrigger effect
  const onFailedRef = useRef(onFailed);
  onFailedRef.current = onFailed;

  const startPolling = useCallback(
    async (taskId: string) => {
      let cancelled = false;
      const startedAt = Date.now();

      // 单独 cleanup hook 在 useEffect return 设
      const cleanup = () => {
        cancelled = true;
      };

      // 立刻进 pending 状态 (UI 可以显示 "生成中" 占位)
      setStatus('pending');

      try {
        while (!cancelled) {
          if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
            if (!cancelled) {
              setStatus('timeout');
              setErrorMsg('生成超时, 请重试');
              onFailedRef.current?.('生成超时, 请重试');
            }
            return cleanup;
          }

          let resp: GetNpcStudioCreateTaskResp | null = null;
          try {
            resp = await postGetNpcStudioCreateTask({ task_id: taskId });
          } catch (e) {
            // network / transport 错: 不立刻 failed (单次抖动可恢复), 继续轮询
            console.error('[useQuickCreatePolling] poll error', e);
          }
          if (cancelled) return cleanup;

          if (resp) {
            const s = resp.status;
            if (s === 'image_done') {
              if (resp.image_url) {
                // updateImgInfo (partial reducer 已存在, packages/share/src/state/edit.ts:660)
                dispatch(updateImgInfo({ base_img_url: resp.image_url }));
              }
              setStatus('image_done');
            } else if (s === 'voice_done') {
              if (resp.voice_id) {
                // WorkspaceNpcVoiceInfo.npc_tone 是 Record<voice_id, weight>;
                // chat-agent 给的 voice_id 包成 { [voice_id]: 1 } 跟 VoiceModal 同款 (state/edit.ts:819)
                dispatch(mergeVoiceInfo({ npc_tone: { [resp.voice_id]: 1 } }));
              }
              setStatus('voice_done');
            } else if (s === 'done') {
              // 终态 — 拿真 npc_id, hot-swap 最后字段
              if (resp.image_url) {
                dispatch(updateImgInfo({ base_img_url: resp.image_url }));
              }
              if (resp.voice_id) {
                dispatch(mergeVoiceInfo({ npc_tone: { [resp.voice_id]: 1 } }));
              }
              const finalNpcId = Number(resp.npc_id) || 0;
              if (finalNpcId > 0) {
                setNpcId(finalNpcId);
                // 加 URL query (shallow:true, 不重跑 SSR) — caller 的 useEffect 监听 query.npc_id
                // 变化触发 dispatch(getWorkspaceNpcDetail({npc_id})) 整体覆盖 NPC editor state
                addQuery({ npc_id: finalNpcId });
              }
              setStatus('done');
              return cleanup;
            } else if (s === 'failed') {
              const errMsg = resp.error_msg || '生成失败, 请重试';
              setErrorMsg(errMsg);
              setStatus('failed');
              onFailedRef.current?.(errMsg);
              return cleanup;
            }
            // pending / 其他: 继续轮询
          }

          await new Promise((res) => setTimeout(res, POLL_INTERVAL_MS));
        }
      } catch (e) {
        if (!cancelled) {
          const errMsg = e instanceof Error ? e.message : '生成失败';
          console.error('[useQuickCreatePolling] fatal', e);
          setErrorMsg(errMsg);
          setStatus('failed');
          onFailedRef.current?.(errMsg);
        }
      }

      return cleanup;
    },
    [dispatch, addQuery]
  );

  useEffect(() => {
    if (!router.isReady) return;
    if (startedRef.current) return;
    const taskId = router.query.create_task_id;
    const taskIdStr = Array.isArray(taskId) ? taskId[0] : taskId;
    if (!taskIdStr) return;

    startedRef.current = true;
    let cleanupFn: (() => void) | undefined;
    startPolling(taskIdStr).then((cleanup) => {
      cleanupFn = cleanup;
    });

    return () => {
      if (cleanupFn) cleanupFn();
    };
  }, [router.isReady, router.query.create_task_id, startPolling]);

  return { status, npcId, errorMsg };
}
